import React from 'react';
import styles from './Projects.module.css';

interface Project {
  title:       string;
  emoji:       string;
  color:       string;
  description: string;
  highlights:  string[];
  tech:        string[];
}

const PROJECTS: Project[] = [
  {
    title: 'Online Education Portal',
    emoji: '🎓',
    color: '#00e5ff',
    description:
      'Cloud-native microservices platform supporting academic workflows and assignment management for worldwide universities. Features high-volume message processing and event-based communication between services.',
    highlights: ['High-volume processing', 'Event-based comms', 'Global scale'],
    tech: ['Java Spring Boot', 'React', 'AWS SQS', 'PostgreSQL', 'MongoDB', 'Docker', 'AWS'],
  },
  {
    title: 'Healthcare System',
    emoji: '🏥',
    color: '#34d399',
    description:
      'Secure REST-based web platform for managing electronic medical records in a distributed environment for the Finnish healthcare industry. Designed for GDPR compliance and high availability.',
    highlights: ['GDPR-compliant', 'Distributed architecture', 'Finnish healthcare'],
    tech: ['Java', 'Spring Boot', 'Angular', 'Docker', 'MariaDB', 'PostgreSQL'],
  },
  {
    title: 'Point of Sale & Payment Platform',
    emoji: '💳',
    color: '#f59e0b',
    description:
      'Event-driven microservices platform for digital invoicing and online payments. Built with Kafka-based async messaging and real-time WebSocket updates. Focused on secure transactions and performance.',
    highlights: ['Real-time updates', 'Kafka messaging', 'Secure transactions'],
    tech: ['Java Spring Boot', 'Node.js', 'React', 'Kafka', 'PostgreSQL', 'MongoDB', 'WebSocket'],
  },
  {
    title: 'Student Management System',
    emoji: '📚',
    color: '#a78bfa',
    description:
      'REST API–driven platform for student records, payments, and administrative automation. Supports multi-role access control and complex reporting workflows.',
    highlights: ['Multi-role access', 'Payment integration', 'Admin automation'],
    tech: ['Java Spring Boot', 'Node.js', 'Angular', 'MySQL', 'MongoDB'],
  },
];

export default function Projects(): React.ReactElement {
  return (
    <section id="projects">
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          <span className={styles.headingNum}>04.</span>
          Projects
          <div className={styles.headingLine} />
        </h2>

        <div className={styles.grid}>
          {PROJECTS.map((project) => (
            <article
              key={project.title}
              className={styles.card}
              onMouseEnter={(e) => (e.currentTarget.style.borderBottomColor = project.color)}
              onMouseLeave={(e) => (e.currentTarget.style.borderBottomColor = 'transparent')}
            >
              <div className={styles.cardHeader}>
                <span className={styles.emoji} aria-hidden="true">{project.emoji}</span>
                <span
                  className={styles.badge}
                  style={{ background: `${project.color}15`, color: project.color }}
                >
                  Featured
                </span>
              </div>

              <h3 className={styles.title}>{project.title}</h3>
              <p className={styles.description}>{project.description}</p>

              <div className={styles.highlights}>
                {project.highlights.map((h) => (
                  <span key={h} className={styles.highlight} style={{ color: project.color }}>
                    ✓ {h}
                  </span>
                ))}
              </div>

              <div className={styles.techRow}>
                {project.tech.map((t, i) => (
                  <React.Fragment key={t}>
                    {i > 0 && <span className={styles.techDot} aria-hidden="true">·</span>}
                    <span className={styles.techItem}>{t}</span>
                  </React.Fragment>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
